/**
 * Reset Password Form Component
 * Material-UI based password reset confirmation form
 * Requirements: 6.7
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordFormData } from '../schemas/auth.schema';
import { useAuth } from '../context/AuthContext';

export const ResetPasswordForm: React.FC = () => {
  const { confirmResetPassword, isLoading, error, clearError } = useAuth();
  const [isSubmitted, setIsSubmitted] = useState(false);
  // /forgot-password から遷移した場合は email を受け取り自動補完する（リロード/直接アクセス時は空欄で入力）
  const location = useLocation();
  const prefilledEmail = (location.state as { email?: string } | null)?.email ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: prefilledEmail,
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      clearError();
      await confirmResetPassword(data.email, data.code, data.newPassword);
      setIsSubmitted(true);
    } catch {
      // Error is handled by AuthContext
    }
  };

  if (isSubmitted) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          mx: 'auto',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          パスワードリセット完了
        </Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          パスワードが正常にリセットされました。新しいパスワードでログインしてください。
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
          >
            ログインへ
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom align="center">
        新しいパスワードを設定
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        メールで受け取った確認コードと新しいパスワードを入力してください。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          label="メールアドレス"
          type="email"
          fullWidth
          margin="normal"
          error={!!errors.email}
          helperText={errors.email?.message}
          autoComplete="email"
          disabled={isLoading}
        />

        <TextField
          {...register('code')}
          label="確認コード"
          fullWidth
          margin="normal"
          error={!!errors.code}
          helperText={errors.code?.message}
          autoFocus
          disabled={isLoading}
        />

        <TextField
          {...register('newPassword')}
          label="新しいパスワード"
          type="password"
          fullWidth
          margin="normal"
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message || 'パスワードは8文字以上で、大文字・小文字・数字を含む必要があります'}
          autoComplete="new-password"
          disabled={isLoading}
        />

        <TextField
          {...register('confirmPassword')}
          label="新しいパスワード（確認）"
          type="password"
          fullWidth
          margin="normal"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          autoComplete="new-password"
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'パスワードを変更'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={Link} to="/login" variant="body2">
            ログインに戻る
          </MuiLink>
        </Box>
      </Box>
    </Paper>
  );
};
